import React from 'react';
import { MessageCircle } from 'lucide-react';
import { whatsappUrl } from '../../lib/whatsapp';

/**
 * One-tap WhatsApp share button. Opens wa.me in a new tab with
 * the chat pre-filled.
 *
 * Required props:
 *   - message: string                    the text body
 *
 * Optional props:
 *   - phone: string                      target number; omit/empty for WhatsApp contact picker
 *   - label: string                      button text (default: "WhatsApp")
 *   - size: 'sm' | 'default' | 'lg' | 'icon'
 *   - className: string                  extra Tailwind classes
 *   - disabled: boolean
 *
 * Usage:
 *   import { WhatsAppButton } from './ui/whatsapp-button';
 *   import { WHATSAPP_TEMPLATES } from '../../lib/whatsapp';
 *
 *   <WhatsAppButton
 *     phone={provider.phone}
 *     message={WHATSAPP_TEMPLATES.providerQuoteRequest({ ... })}
 *     label="WhatsApp"
 *     size="sm"
 *   />
 */
export function WhatsAppButton({
  phone,
  message,
  label = 'WhatsApp',
  size = 'sm',
  className = '',
  disabled = false,
}) {
  if (!message) return null;

  const href = whatsappUrl({ phone, message });

  const sizeClasses = {
    sm: 'h-9 px-3 text-sm',
    default: 'h-10 px-4 text-sm',
    lg: 'h-11 px-8 text-base',
    icon: 'h-9 w-9 p-0',
  }[size] ?? 'h-9 px-3 text-sm';

  return (
    <a
      href={disabled ? undefined : href}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={disabled}
      aria-label={label}
      className={[
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium',
        'bg-green-600 hover:bg-green-700 text-white transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500',
        disabled ? 'pointer-events-none opacity-50' : '',
        sizeClasses,
        className,
      ].filter(Boolean).join(' ')}
    >
      <MessageCircle className="h-4 w-4" aria-hidden="true" />
      {size !== 'icon' && <span>{label}</span>}
    </a>
  );
}

export default WhatsAppButton;
